import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { GuestHelpDeskModal } from '../components/GuestHelpDeskModal';

const styles: Record<string, React.CSSProperties> = {
  container: {
    // Layout handled by .login-page CSS class
  },
  card: {
    // Layout handled by .login-page__card CSS class
  },
  title: {
    color: '#1a6b3c',
    marginBottom: '0.5rem',
    fontSize: '1.5rem',
    textAlign: 'center' as const,
  },
  subtitle: {
    color: '#666',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
    textAlign: 'center' as const,
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '44px',
    textAlign: 'center' as const,
    marginTop: '1rem',
    color: '#1a6b3c',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
};

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [funFacts, setFunFacts] = useState<string[]>([]);
  const [failCount, setFailCount] = useState(0);
  const [showHelpDesk, setShowHelpDesk] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  useEffect(() => {
    apiClient<Array<{ title: string; description: string }>>('/credentials/fun-facts/Streets%20%26%20Places')
      .then((items) => setFunFacts(items.map(i => i.title)))
      .catch(() => {});
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      // Forced first-login password change removed — guests are no longer sent to
      // /change-password. Attendees land on My Weekend; everyone else on their prior page.
      if (result.user?.role === 'Attendee') {
        navigate('/my-weekend', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      const msg = apiError.message || '';
      // Detect network/API unreachable errors
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('ECONNREFUSED') || msg.includes('503') || msg.includes('502')) {
        setError('The system is currently under maintenance. Please try again in a few minutes.');
      } else {
        setError(msg || 'Login failed. Please check your credentials.');
        setFailCount(prev => prev + 1);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page attendee-page">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
      <div className="login-page__card">
        <h1 style={styles.title}>KDU Georgia 2026</h1>
        <img src="/KDULogo.PNG" alt="KDU" style={{ width: '80px', height: '80px', objectFit: 'contain', margin: '0 auto 12px', display: 'block', borderRadius: '12px' }} />
        <p style={styles.subtitle}>Sign in to the Reunion Hub</p>

        {error && <div style={styles.error}>{error}</div>}

        {/* Progressive login hints — appear after 3 failed attempts */}
        {failCount >= 3 && (
          <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px 14px', marginBottom: '1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e', marginBottom: '6px' }}>
              💡 Need help logging in?
            </div>
            <ul style={{ margin: '0 0 10px', paddingLeft: '18px', fontSize: '0.78rem', color: '#78350f', lineHeight: 1.6 }}>
              <li>If you have a <strong>ticket number</strong> (e.g. TKT-000), your initial password is the same ticket number</li>
              <li>Make sure to include the "TKT-" prefix and leading zeros</li>
              <li>If you changed your password and forgot it, use the link below to recover it</li>
            </ul>
            <Link
              to="/get-credentials"
              style={{ display: 'block', textAlign: 'center', padding: '10px 16px', backgroundColor: '#1a6b3c', color: '#fff', borderRadius: '8px', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none' }}
            >
              🔑 Get My Credentials
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="email">Ticket Number</label>
            <input
              id="email"
              type="text"
              style={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              placeholder="TKT-000"
            />
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '4px' }}>
              Enter your ticket number (e.g. TKT-000)
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="password">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                style={{ ...styles.input, paddingRight: '2.5rem' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#666', padding: '10px', minWidth: '44px', minHeight: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <Link to="/get-credentials" style={styles.link}>
          Need your login info? Get My Credentials
        </Link>

        <button
          type="button"
          onClick={() => setShowInstructions(true)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
            width: '100%', minHeight: '48px', marginTop: '0.75rem', padding: '0.7rem',
            backgroundColor: '#1a6b3c', color: '#fff', border: 'none',
            borderRadius: '10px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          📖 How to Log In
        </button>

        <button
          type="button"
          onClick={() => setShowHelpDesk(true)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
            width: '100%', minHeight: '48px', marginTop: '0.5rem', padding: '0.7rem',
            backgroundColor: '#fff', color: '#1a6b3c', border: '2px solid #1a6b3c',
            borderRadius: '10px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          🙋 Having trouble? Get live help
        </button>

        {funFacts.length > 0 && (
          <div style={{ marginTop: '1rem', overflow: 'hidden', backgroundColor: '#c9a84c', borderRadius: '4px', padding: '0.4rem 0' }}>
            <div style={{ display: 'flex', whiteSpace: 'nowrap', animation: 'marquee 30s linear infinite' }}>
              {[...funFacts, ...funFacts].map((fact, i) => (
                <span key={i} style={{ display: 'inline-block', padding: '0 2rem', fontSize: '0.75rem', color: '#fff', fontWeight: 500 }}>
                  🏛️ {fact}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center' as const, marginTop: 'auto', paddingTop: '1.5rem', paddingBottom: '1rem' }}>
        <Link to="/privacy" style={{ fontSize: '0.75rem', color: '#999', textDecoration: 'none' }}>
          Privacy Policy
        </Link>
      </div>

      {showHelpDesk && <GuestHelpDeskModal onClose={() => setShowHelpDesk(false)} />}
      {showInstructions && <LoginInstructionsModal onClose={() => setShowInstructions(false)} />}
    </div>
  );
}

// ── Login Instructions Modal — 5 steps ─────────────────────────
const LOGIN_STEPS: { icon: string; title: string; body: string }[] = [
  { icon: '🎫', title: 'Find your ticket number', body: 'It looks like TKT-042 and is on your ticket confirmation. Don\'t have it? Tap "Get My Credentials" below to look it up with your order number and last name.' },
  { icon: '⌨️', title: 'Enter your ticket number', body: 'Type it into the Ticket Number field — include the "TKT-" prefix and leading zeros (e.g. TKT-042).' },
  { icon: '🔑', title: 'Use the same number as your password', body: 'For your first login, your password is the same as your ticket number.' },
  { icon: '➡️', title: 'Tap Sign In', body: 'That\'s it — you\'re in.' },
  { icon: '🎉', title: 'Explore My Weekend', body: 'See your seat, meal, schedule, and event details. You can set a personal password anytime from Settings.' },
];

const CREDENTIAL_STEPS: { icon: string; title: string; body: string }[] = [
  { icon: '📧', title: 'Find your Order ID', body: 'It\'s in the confirmation email you received when you purchased your tickets (labeled Order or Reference number).' },
  { icon: '🔎', title: 'Open "Get My Credentials"', body: 'Tap the button below (also on the login screen).' },
  { icon: '📝', title: 'Enter your Order ID and last name', body: 'Use the last name of whoever purchased the tickets. Double-barrelled names like Bankole-Jines work in full or either part.' },
  { icon: '🎫', title: 'See everyone\'s ticket numbers', body: 'You\'ll get the ticket number for each guest in your order — that\'s your username and first-time password.' },
  { icon: '↩️', title: 'Go back and log in', body: 'Return to the login screen and sign in with your ticket number. Still stuck? Tap "Get live help".' },
];

function LoginInstructionsModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<'login' | 'credentials'>('login');
  const steps = tab === 'login' ? LOGIN_STEPS : CREDENTIAL_STEPS;
  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="How to log in"
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
      >
        <div style={{ background: 'linear-gradient(135deg, #1a6b3c, #0f4a28)', color: '#fff', padding: '1.1rem 1.25rem', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>📖 Login Help</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setTab('login')}
            style={{ flex: 1, padding: '0.7rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, color: tab === 'login' ? '#1a6b3c' : '#9ca3af', borderBottom: tab === 'login' ? '2px solid #1a6b3c' : '2px solid transparent' }}
            aria-current={tab === 'login' ? 'page' : undefined}
          >
            How to Log In
          </button>
          <button
            onClick={() => setTab('credentials')}
            style={{ flex: 1, padding: '0.7rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, color: tab === 'credentials' ? '#1a6b3c' : '#9ca3af', borderBottom: tab === 'credentials' ? '2px solid #1a6b3c' : '2px solid transparent' }}
            aria-current={tab === 'credentials' ? 'page' : undefined}
          >
            No Credentials?
          </button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {tab === 'credentials' && (
            <p style={{ fontSize: '0.82rem', color: '#4b5563', margin: '0 0 1rem', lineHeight: 1.5 }}>
              Don't have your ticket number? Here's how to get it:
            </p>
          )}
          {steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.75rem', marginBottom: i < steps.length - 1 ? '1rem' : 0 }}>
              <div style={{ flexShrink: 0, width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#1a6b3c', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem' }}>
                {i + 1}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f2d1f' }}>{step.icon} {step.title}</div>
                <div style={{ fontSize: '0.82rem', color: '#4b5563', lineHeight: 1.5, marginTop: '2px' }}>{step.body}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: '0 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {tab === 'credentials' && (
            <Link
              to="/get-credentials"
              onClick={onClose}
              style={{ display: 'block', textAlign: 'center', padding: '0.75rem', backgroundColor: '#1a6b3c', color: '#fff', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 700, textDecoration: 'none' }}
            >
              🔑 Get My Credentials
            </Link>
          )}
          <button
            onClick={onClose}
            style={{ width: '100%', padding: '0.75rem', backgroundColor: tab === 'credentials' ? '#f5f5f5' : '#1a6b3c', color: tab === 'credentials' ? '#333' : '#fff', border: tab === 'credentials' ? '1px solid #e0e0e0' : 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}
          >
            {tab === 'credentials' ? 'Close' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}
